import React from 'react';
import styled from 'styled-components';
import { Button } from '../../atoms/Button';
import { SearchBar } from '../../atoms/SearchBar';
import { Select, SelectOptionType } from '../../atoms/Select';
import { Checkbox } from '../../atoms/Checkbox';
import { PermissionGate } from '../../atoms/PermissionGate';
import { Resource, Permission } from '../../../constants/permissions';
import { useResponsive } from '../../../hooks/useResponsive';

interface PropertySearchFormProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusValue: string;
  onStatusChange: (value: string) => void;
  typeValue: string;
  onTypeChange: (value: string) => void;
  typeOptions: { id: number; name: string }[];
  onCreateClick: () => void;
  createButtonText?: string;
  searchPlaceholder?: string;
  statusPlaceholder?: string;
  typePlaceholder?: string;
  className?: string;
  isCreateButtonDisabled?: boolean;
  hotValue: string;
  onHotChange: (value: string) => void;
}

const PROPERTY_STATUS_OPTIONS: SelectOptionType[] = [
  { id: 'rent', name: 'Rent', value: 'rent' },
  { id: 'sale', name: 'Sale', value: 'sale' },
];

export const PropertySearchForm: React.FC<PropertySearchFormProps> = ({
  searchValue,
  onSearchChange,
  statusValue,
  onStatusChange,
  typeValue,
  onTypeChange,
  typeOptions,
  onCreateClick,
  createButtonText = 'Create',
  searchPlaceholder = 'Search by property name and code...',
  statusPlaceholder = 'Property status',
  typePlaceholder = 'Property type',
  className,
  isCreateButtonDisabled = false,
  hotValue,
  onHotChange,
}) => {
  const { isMobile, isTablet } = useResponsive();

  // Map typeOptions to SelectOptionType
  const propertyTypeOptions: SelectOptionType[] = [
    ...typeOptions.map(type => ({
      id: type.id,
      name: type.name,
      value: type.name,
    })),
  ];

  return (
    <FormContainer className={className}>
      <SearchBar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        placeholder={searchPlaceholder}
        style={{
          minWidth: isMobile ? '100%' : isTablet ? 180 : 200,
          maxWidth: isMobile ? '100%' : 380,
          height: isMobile ? 44 : 48,
          flex: isMobile ? '1' : '1 1 200px',
          marginBottom: 0,
          width: isMobile ? '100%' : 'auto'
        }}
      />

      <StyledSelectWrapper>
        <Select
          name="propertyStatus"
          options={PROPERTY_STATUS_OPTIONS}
          placeholder={statusPlaceholder}
          defaultValue={statusValue}
          onChange={(_, value) => onStatusChange(value)}
          width="100%"
          hasSearchInput={false}
        />
      </StyledSelectWrapper>

      <StyledSelectWrapper>
        <Select
          name="propertyType"
          options={propertyTypeOptions}
          placeholder={typePlaceholder}
          defaultValue={typeValue}
          onChange={(_, value) => onTypeChange(value)}
          width="100%"
          hasSearchInput={false}
        />
      </StyledSelectWrapper>

      <StyledCheckboxWrapper>
        <Checkbox
          name="isHotFilter"
          variant="empty"
          label="Hot Properties"
          checked={hotValue === 'true'}
          onChange={(checked) => onHotChange(checked ? 'true' : '')}
        />
      </StyledCheckboxWrapper>

      <PermissionGate
        resource={Resource.PROPERTIES}
        permission={Permission.CREATE}
      >
        <ButtonWrapper>
          <Button
            text={createButtonText}
            variant="filled"
            onClick={onCreateClick}
            disabled={isCreateButtonDisabled}
          />
        </ButtonWrapper>
      </PermissionGate>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 1200px) {
    gap: 12px;
    flex-wrap: wrap;
  }

  @media (max-width: 1000px) {
    gap: 10px;
    flex-wrap: wrap;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-bottom: 12px;
  }
`;

const StyledSelectWrapper = styled.div`
  min-width: 180px;
  max-width: 220px;
  height: 55px;
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  
  @media (max-width: 1200px) {
    min-width: 160px;
    max-width: 200px;
  }

  @media (max-width: 1000px) {
    min-width: 140px;
    max-width: 180px;
  }

  @media (max-width: 768px) {
    height: 48px;
  }

  @media (max-width: 600px) {
    min-width: 100%;
    max-width: 100%;
    height: 48px;
    flex: 1;
    margin-right: 0;
  }

  @media (max-width: 480px) {
    height: 44px;
  }
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 55px;
  padding: 0 8px;
  
  @media (max-width: 768px) {
    height: 48px;
  }

  @media (max-width: 600px) {
    width: 100%;
    margin-bottom: 8px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 55px;
  margin-left: auto;
  flex-shrink: 0;
  
  @media (max-width: 1200px) {
    margin-left: auto;
  }

  @media (max-width: 1000px) {
    margin-left: 8px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    height: 48px;
  }
  
  @media (max-width: 600px) {
    width: auto;
    justify-content: flex-end;
    height: 48px;
    margin-left: auto;
  }

  @media (max-width: 480px) {
    height: 44px;
  }
`;
